import { Star, BadgeCheck } from 'lucide-react';
import type { ProductReview } from '../../types';

interface ReviewCardProps {
    review: ProductReview;
}

// Format relative time (e.g., "il y a 2 jours")
function formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return 'Hier';
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaines`;
    if (diffDays < 365) return `Il y a ${Math.floor(diffDays / 30)} mois`;
    return `Il y a ${Math.floor(diffDays / 365)} ans`;
}

export function ReviewCard({ review }: ReviewCardProps) {
    return (
        <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
            {/* Header: Name, Verified Badge, Date */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {/* Avatar */}
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {review.customerName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-gray-900 text-sm">
                                {review.customerName}
                            </span>
                            {review.isVerified && (
                                <span className="flex items-center gap-0.5 text-green-600 text-xs">
                                    <BadgeCheck className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">Vérifié</span>
                                </span>
                            )}
                        </div>
                        <span className="text-xs text-gray-400">
                            {formatRelativeTime(review.createdAt)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Stars */}
            <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                        key={star}
                        className={`w-4 h-4 ${star <= review.rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-200'
                            }`}
                    />
                ))}
            </div>

            {/* Title (if present) */}
            {review.title && (
                <p className="font-semibold text-gray-900 text-sm">{review.title}</p>
            )}

            {/* Comment */}
            <p className="text-gray-600 text-sm leading-relaxed">{review.comment}</p>
        </div>
    );
}
